import { jsx as _jsx } from "react/jsx-runtime";
import { useCombobox } from 'downshift';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import { isNumber } from '../../../common.types.js';
import { filterPredicateMap as filterFnMap, itemToString } from '../BKJComboBox.utils.js';
import { useBKJComboBoxContextHooks } from './BKJComboBox.context.hooks.js';
export const BKJComboBoxContext = createContext({});
function extractInitialSelectedItem({ value, options, type }) {
    if (type === 'Multiple')
        return undefined;
    if (value.length === 1) {
        return options.find((option) => String(option.value) === String(value[0]));
    }
    return undefined;
}
export const BKJComboBoxProvider = ({ options: __options__, label, type, variant, placeholder, filter, onChange, name, value, error, action, portalTargetElement, leftIconProps, disabled, required, children, overrides, onIsOpenChange: __onIsOpenChange__, }) => {
    const [{ inputValue, anchorElement, dropdownElement }, { setInputValue, setAnchorElement, setDropdownElement },] = useBKJComboBoxContextHooks();
    const [isDropdownReverse, setIsDropdownReverse] = useState(false);
    useEffect(() => {
        if (dropdownElement) {
            const observer = new MutationObserver(() => {
                const isReverse = dropdownElement.style.top === 'auto' && dropdownElement.style.bottom === '0px';
                setIsDropdownReverse(isReverse);
            });
            observer.observe(dropdownElement, { attributes: true, attributeFilter: ['style'] });
            return () => {
                observer.disconnect();
            };
        }
        return void 0;
    }, [dropdownElement]);
    const isLabelValueShown = !overrides.includes('HIDE_VALUE_LABEL');
    const filterFn = filterFnMap[filter];
    const options = filterFn(inputValue, __options__);
    const parentRef = useRef(null);
    const estimateSize = useCallback(() => 56, []);
    const rowVirtualizer = useVirtual({
        size: options.length,
        parentRef,
        estimateSize,
        overscan: 2,
        keyExtractor: (index) => options[index].id,
    });
    const { reset, isOpen, openMenu, closeMenu, toggleMenu, selectedItem, selectItem, getMenuProps, getItemProps, getInputProps, getLabelProps, getComboboxProps, highlightedIndex, setHighlightedIndex, getToggleButtonProps, setInputValue: __downShiftSetInputValue__, } = useCombobox({
        inputValue,
        itemToString,
        items: options,
        initialSelectedItem: extractInitialSelectedItem({
            value,
            options,
            type,
        }),
        // WEB-4811 / FR-005b / I-09: on every open transition, highlight the
        // currently selected option if any, else the first option. Implemented
        // as a stateReducer so the highlight is set synchronously with the open
        // transition and `aria-activedescendant` is correct on the first render.
        //
        // We detect "menu just opened" by comparing state.isOpen (previous) with
        // changes.isOpen (next). This catches every open path — focus, click,
        // ArrowDown, openMenu() / toggleMenu() calls — without enumerating
        // Downshift's action types, which is both cleaner and upgrade-safe.
        stateReducer: (state, actionAndChanges) => {
            const { changes } = actionAndChanges;
            if (type === 'Multiple')
                return changes;
            const isOpening = !state.isOpen && changes.isOpen === true;
            if (isOpening && (changes.highlightedIndex ?? -1) < 0) {
                const activeSelectedItem = changes.selectedItem ?? state.selectedItem;
                const selectedIdx = activeSelectedItem
                    ? options.findIndex((opt) => String(opt.value) === String(activeSelectedItem.value))
                    : -1;
                const desired = selectedIdx >= 0 ? selectedIdx : options.length > 0 ? 0 : -1;
                return { ...changes, highlightedIndex: desired };
            }
            return changes;
        },
        scrollIntoView: () => void 0,
        onInputValueChange: ({ inputValue: __inputValue__, isOpen: __isOpen__ }) => {
            if (__isOpen__)
                setInputValue(__inputValue__ || '');
            if (!isLabelValueShown) {
                if (!__isOpen__)
                    setInputValue('');
            }
        },
        onHighlightedIndexChange: ({ highlightedIndex: __i__ }) => {
            if (isNumber(__i__) && __i__ >= 0)
                rowVirtualizer.scrollToIndex(__i__);
        },
        onSelectedItemChange: (event) => {
            switch (type) {
                case 'Multiple': {
                    const { selectedItem } = event;
                    const selectedItemValue = String(selectedItem?.value || '');
                    const target = {
                        ...selectedItem,
                        name,
                        value: value.includes(selectedItemValue)
                            ? value.filter((strValue) => strValue !== selectedItemValue)
                            : [...value, selectedItemValue],
                    };
                    if (isLabelValueShown)
                        __downShiftSetInputValue__(target?.label || '');
                    else
                        __downShiftSetInputValue__('');
                    if (onChange)
                        onChange({ target });
                    return void 0;
                }
                case 'Single': {
                    const { selectedItem } = event;
                    const target = {
                        ...selectedItem,
                        name,
                        value: selectedItem?.value ? [selectedItem?.value] : undefined,
                    };
                    if (isLabelValueShown)
                        __downShiftSetInputValue__(target?.label || '');
                    else
                        __downShiftSetInputValue__('');
                    if (onChange)
                        onChange({ target });
                    closeMenu();
                    anchorElement?.blur();
                    return void 0;
                }
            }
        },
        onIsOpenChange: ({ isOpen: __isOpen__, highlightedIndex: __highlightedIndex__ }) => {
            if (__onIsOpenChange__)
                __onIsOpenChange__({ isOpen: !!__isOpen__, highlightedIndex: __highlightedIndex__ });
            if (__isOpen__) {
                if (isNumber(__highlightedIndex__) && __highlightedIndex__ >= 0) {
                    rowVirtualizer.scrollToIndex(__highlightedIndex__, { align: 'auto' });
                }
            }
            __downShiftSetInputValue__('');
        },
    });
    const handleInputFocus = useCallback(openMenu, [openMenu]);
    const handleMultiSelectClick = useCallback((event) => {
        if (type === 'Multiple') {
            const selectedItem = options.find((option) => option.value === event.target.value);
            const selectedItemValue = String(selectedItem?.value || '');
            const target = {
                ...selectedItem,
                name,
                value: value.includes(selectedItemValue)
                    ? value.filter((strValue) => strValue !== selectedItemValue)
                    : [...value, selectedItemValue],
            };
            if (isLabelValueShown)
                __downShiftSetInputValue__(target?.label || '');
            else
                __downShiftSetInputValue__('');
            if (onChange)
                onChange({ target });
        }
    }, [__downShiftSetInputValue__, isLabelValueShown, name, onChange, options, type, value]);
    const handleSelectClear = useCallback(() => {
        selectItem(undefined);
        onChange({ target: { name, value: [] } });
    }, [name, onChange, selectItem]);
    useEffect(() => {
        if (options.length === 0)
            setHighlightedIndex(-1);
        else if (inputValue !== '' && highlightedIndex === -1 && options.length > 0)
            setHighlightedIndex(0);
    }, [highlightedIndex, inputValue, options.length, setHighlightedIndex]);
    return (_jsx(BKJComboBoxContext.Provider, { value: {
            type,
            name,
            label,
            reset,
            value,
            error,
            action,
            isOpen,
            filter,
            options,
            variant,
            onChange,
            required,
            disabled,
            openMenu,
            parentRef,
            closeMenu,
            inputValue,
            toggleMenu,
            placeholder,
            selectedItem,
            getItemProps,
            getMenuProps,
            getInputProps,
            getLabelProps,
            anchorElement,
            leftIconProps,
            rowVirtualizer,
            dropdownElement,
            setAnchorElement,
            highlightedIndex,
            getComboboxProps,
            isDropdownReverse,
            setDropdownElement,
            portalTargetElement,
            setHighlightedIndex,
            getToggleButtonProps,
            handleMultiSelectClick,
            onInputFocus: handleInputFocus,
            onSelectedClear: handleSelectClear,
        }, children: children }));
};
export const useBKJComboBoxContext = () => useContext(BKJComboBoxContext);
//# sourceMappingURL=BKJComboBox.context.js.map